import { Button, Card, CardHeader, CardHeaderActions, CardHeading, ConfirmationDialog, Id } from "@dgs/core";
import { useQueryClient } from "@tanstack/react-query";
import { FC } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import styled from "styled-components";
import { birthdayWidgetService } from "../../dashboard/widgets/birthdays/service";
import { contactService } from "../../shared/api/contactService";
import { TenantArchiveDrawer } from "../../shared/tenantArchive/TenantArchiveDrawer";
import { useRestoreByTenantHandler } from "../../shared/tenantArchive/useRestoreByTenantHandler";

interface Props {
	id: Id;
	className?: string;
	shouldNavigate?: boolean;
}

const WarningCard = styled(Card)`
	background-color: ${({ theme }) => theme.colors.palette.warning.light.base};
`;

export const ArchivedContactBanner: FC<Props> = ({ id, className, shouldNavigate = true }) => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const {
		clickHandler: restoreClickHandler,
		drawerProps,
		confirmationProps,
	} = useRestoreByTenantHandler(contactService, () => {
		void queryClient.invalidateQueries({ queryKey: birthdayWidgetService.key });
		shouldNavigate && navigate(`/contacts/active/${id}`, { replace: true });
	});

	return (
		<WarningCard className={className}>
			<CardHeader>
				<CardHeading>{t("This contact is archived in at least this tenant")}</CardHeading>
				<CardHeaderActions>
					<Button onClick={() => restoreClickHandler(id)} title={t("Restore")} size="small" type="button">
						{t("Restore")}
					</Button>
					<TenantArchiveDrawer {...drawerProps} title={t("Restore")} type="restore" service={contactService} />
					<ConfirmationDialog
						{...confirmationProps}
						heading={t("Restore")}
						labels={{ close: t("Close"), confirm: t("Confirm") }}
					>
						{t("Are you sure you want to restore this contact?")}
					</ConfirmationDialog>
				</CardHeaderActions>
			</CardHeader>
		</WarningCard>
	);
};
